import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, IconButton, Input, Select, Accordion, Table } from '../components/ui';
import { UserGroupIcon, PlusIcon, MagnifyingGlassIcon, ArrowTopRightOnSquareIcon, CalendarDaysIcon, UserCircleIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useClients, useAllSurveys, useCompileSurvey, useUpcomingTasks, useActivitiesPaginated, useEngagementTemplates, useCreateTemplate, useUpdateTemplate, useDeleteTemplate } from '../hooks/useClients';
import { useWorkshops, useApproveWorkshop, useDenyWorkshop, useBackfillCalendarLinks } from '../hooks/useWorkshops';
import FilterableTable from '../components/FilterableTable';

// ── Shared tab style ─────────────────────────────────────────────
const TABS = [
  { id: 'clients',   label: 'Clients'        },
  { id: 'surveys',   label: 'Surveys'        },
  { id: 'upcoming',  label: 'Upcoming Tasks' },
  { id: 'audit',     label: 'Audit Trail'    },
  { id: 'workshops', label: 'Workshops'      },
  { id: 'templates', label: 'Templates'      },
];

// ── Status / size config ─────────────────────────────────────────
const STATUS_CONFIG = {
  active:    { label: 'Active',    cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  paused:    { label: 'Paused',    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
};

const ORG_SIZE_LABEL   = { small: 'Small', medium: 'Medium', large: 'Large', enterprise: 'Enterprise' };
const ORG_SIZE_OPTIONS = [
  { value: 'small',      label: 'Small' },
  { value: 'medium',     label: 'Medium' },
  { value: 'large',      label: 'Large' },
  { value: 'enterprise', label: 'Enterprise' },
];
const PHASE_OPTIONS = [
  { value: 'discovery',      label: 'Discovery' },
  { value: 'strategy',       label: 'Strategy' },
  { value: 'pilot_planning', label: 'Pilot Planning' },
  { value: 'execution',      label: 'Execution' },
];

function formatDate(ts) {
  if (!ts) return '—';
  // Firestore Timestamp from Admin SDK serialises as { _seconds, _nanoseconds }
  if (ts && typeof ts === 'object' && '_seconds' in ts) {
    return new Date(ts._seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  // Firestore Timestamp client object (has .toDate())
  if (ts && typeof ts.toDate === 'function') {
    return ts.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const baseUrl = (process.env.REACT_APP_BASE_URL || window.location.origin).replace(/\/$/, '');

// ── Clients tab ──────────────────────────────────────────────────
function ClientsTab({ clients, isLoading, navigate }) {
  const [search,         setSearch]         = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [sizeFilter,     setSizeFilter]     = useState('');
  const [phaseFilter,    setPhaseFilter]    = useState('');

  const industryOptions = useMemo(() => {
    const unique = [...new Set(clients.map(c => c.industry).filter(Boolean))].sort();
    return unique.map(i => ({ value: i, label: i }));
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter(c => {
      if (q && !c.name.toLowerCase().includes(q) && !(c.industry || '').toLowerCase().includes(q)) return false;
      if (statusFilter   && c.consultingStatus  !== statusFilter)                   return false;
      if (industryFilter && c.industry          !== industryFilter)                 return false;
      if (sizeFilter     && c.organisationSize  !== sizeFilter)                     return false;
      if (phaseFilter    && !(c.selectedPhases || []).includes(phaseFilter))        return false;
      return true;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, search, statusFilter, industryFilter, sizeFilter, phaseFilter]);

  if (!isLoading && clients.length === 0) {
    return (
      <div className="text-center py-20">
        <UserGroupIcon className="w-12 h-12 mx-auto mb-3 text-text-muted-light dark:text-text-muted opacity-30" />
        <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary mb-1">No clients yet</h2>
        <p className="text-text-muted-light dark:text-text-muted mb-6">Start by onboarding your first consulting client.</p>
        <Button variant="primary" onClick={() => navigate('/cms/companies/new')}>
          <PlusIcon className="w-4 h-4 mr-1.5 inline-block" />
          Onboard New Client
        </Button>
      </div>
    );
  }

  return (
    <Card>
      <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-text-secondary-light dark:text-text-secondary" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="pl-10 w-full" />
        </div>
        <div className="sm:w-40">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="paused">Paused</option>
          </Select>
        </div>
        <div className="sm:w-40">
          <Select value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)}>
            <option value="">All industries</option>
            {industryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="sm:w-36">
          <Select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)}>
            <option value="">All sizes</option>
            {ORG_SIZE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="sm:w-40">
          <Select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
            <option value="">All phases</option>
            {PHASE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
      </div>

      <FilterableTable
        data={filtered.map((client) => ({ ...client, onClick: () => navigate(`/clients/${client.id}`) }))}
        showFilters={false}
        defaultItemsPerPage={25}
        defaultSortColumn="name"
        defaultSortDirection="asc"
        loading={isLoading}
        columns={[
          {
            key: 'name',
            header: 'Client',
            align: 'left',
            className: 'font-semibold text-text-primary-light dark:text-text-primary cursor-pointer hover:opacity-70 transition',
            render: (c) => c.name,
          },
          {
            key: 'industry',
            header: 'Industry',
            align: 'left',
            className: 'text-text-secondary-light dark:text-text-secondary',
            render: (c) => c.industry || '—',
          },
          {
            key: 'organisationSize',
            header: 'Size',
            align: 'left',
            className: 'text-text-secondary-light dark:text-text-secondary',
            render: (c) => ORG_SIZE_LABEL[c.organisationSize] || '—',
          },
          {
            key: 'consultingStatus',
            header: 'Status',
            align: 'left',
            render: (c) => {
              const cfg = STATUS_CONFIG[c.consultingStatus];
              return cfg
                ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}>{cfg.label}</span>
                : '—';
            },
            sortValue: (c) => c.consultingStatus || '',
          },
        ]}
      />
    </Card>
  );
}

// ── Surveys tab ──────────────────────────────────────────────────
function SurveysTab({ navigate }) {
  const { data: surveys = [], isLoading, isError } = useAllSurveys();
  const compileSurvey = useCompileSurvey();
  const [filter, setFilter]             = useState('all');
  const [compilingAll, setCompilingAll] = useState(false);
  const [compileErrors, setCompileErrors] = useState({});

  const FILTER_OPTIONS = [
    { value: 'all',     label: 'All surveys' },
    { value: 'pending', label: 'Pending compile' },
    { value: 'open',    label: 'Open' },
    { value: 'closed',  label: 'Closed' },
  ];

  const filtered = useMemo(() => {
    return surveys.filter((s) => {
      if (filter === 'pending') return s.uncompiledCount > 0;
      if (filter === 'open')    return s.isOpen;
      if (filter === 'closed')  return !s.isOpen;
      return true;
    });
  }, [surveys, filter]);

  // Group by company, sorted by most recent survey within each group
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((s) => {
      if (!map[s.companyId]) {
        map[s.companyId] = { companyName: s.companyName, companyId: s.companyId, surveys: [] };
      }
      map[s.companyId].surveys.push(s);
    });
    Object.values(map).forEach((g) => {
      g.surveys.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    });
    return Object.values(map).sort((a, b) => {
      const aDate = a.surveys[0]?.createdAt || '';
      const bDate = b.surveys[0]?.createdAt || '';
      return bDate.localeCompare(aDate);
    });
  }, [filtered]);

  const pendingSurveys = surveys.filter((s) => s.uncompiledCount > 0);

  const handleCompile = async (survey) => {
    setCompileErrors((prev) => ({ ...prev, [survey.id]: null }));
    try {
      await compileSurvey.mutateAsync({ clientId: survey.companyId, surveyId: survey.id });
    } catch (err) {
      setCompileErrors((prev) => ({
        ...prev,
        [survey.id]: err.response?.data?.error || 'Compile failed.',
      }));
    }
  };

  const handleCompileAll = async () => {
    setCompilingAll(true);
    setCompileErrors({});
    const errors = {};
    for (const s of pendingSurveys) {
      try {
        await compileSurvey.mutateAsync({ clientId: s.companyId, surveyId: s.id });
      } catch (err) {
        errors[s.id] = err.response?.data?.error || 'Compile failed.';
      }
    }
    setCompileErrors(errors);
    setCompilingAll(false);
  };

  if (isLoading) return <p className="text-sm text-text-muted-light dark:text-text-muted py-6">Loading surveys…</p>;
  if (isError)   return <p className="text-sm text-red-500 py-6">Failed to load surveys.</p>;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} options={FILTER_OPTIONS} />
        {pendingSurveys.length > 0 && (
          <Button variant="primary" size="sm" onClick={handleCompileAll} disabled={compilingAll}>
            {compilingAll ? 'Compiling…' : `Compile All Pending (${pendingSurveys.length})`}
          </Button>
        )}
      </div>

      {surveys.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-sm text-text-secondary-light dark:text-text-secondary">No surveys yet.</p>
          </div>
        </Card>
      ) : grouped.length === 0 ? (
        <p className="text-sm text-text-muted-light dark:text-text-muted py-4">No surveys match the current filter.</p>
      ) : (
        grouped.map((group) => (
          <div key={group.companyId} className="space-y-2">
            {/* Company header */}
            <button
              onClick={() => navigate(`/clients/${group.companyId}/surveys`)}
              className="text-sm font-semibold text-text-primary-light dark:text-text-primary hover:opacity-70 transition text-left"
            >
              {group.companyName}
            </button>

            {group.surveys.map((survey) => {
              const surveyUrl = `${baseUrl}/survey/${survey.id}`;
              return (
                <div
                  key={survey.id}
                  className="border border-border-light dark:border-border rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap bg-white dark:bg-surface-dark"
                >
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-text-primary-light dark:text-text-primary truncate">
                        {survey.title}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                        survey.isOpen
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {survey.isOpen ? 'Open' : 'Closed'}
                      </span>
                      {survey.uncompiledCount > 0 && (
                        <span className="text-xs text-amber-600 dark:text-amber-400 font-medium flex-shrink-0">
                          {survey.uncompiledCount} pending
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs text-text-muted-light dark:text-text-muted">
                        Created {formatDate(survey.createdAt)}
                      </span>
                      <span className="text-xs text-text-muted-light dark:text-text-muted">
                        {survey.responseCount} response{survey.responseCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {compileErrors[survey.id] && (
                      <span className="text-xs text-red-500">{compileErrors[survey.id]}</span>
                    )}
                    {survey.uncompiledCount > 0 && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleCompile(survey)}
                        disabled={compileSurvey.isPending && compileSurvey.variables?.surveyId === survey.id}
                      >
                        {compileSurvey.isPending && compileSurvey.variables?.surveyId === survey.id
                          ? 'Compiling…'
                          : `Compile ${survey.uncompiledCount}`}
                      </Button>
                    )}
                    <a
                      href={surveyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-text-muted-light dark:text-text-muted hover:opacity-70 transition"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                      View
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}

// ── Audit trail config ───────────────────────────────────────────
const ACTIVITY_CATEGORY = {
  activity_checked:         { label: 'Activity',    cat: 'engagement' },
  activity_unchecked:       { label: 'Activity',    cat: 'engagement' },
  deliverable_updated:      { label: 'Deliverable', cat: 'engagement' },
  phase_added:              { label: 'Phase',       cat: 'engagement' },
  phase_deleted:            { label: 'Phase',       cat: 'engagement' },
  phase_dates_updated:      { label: 'Phase',       cat: 'engagement' },
  template_applied:         { label: 'Template',    cat: 'engagement' },
  document_uploaded:        { label: 'Document',    cat: 'document'   },
  document_updated:         { label: 'Document',    cat: 'document'   },
  document_deleted:         { label: 'Document',    cat: 'document'   },
  survey_created:           { label: 'Survey',      cat: 'survey'     },
  survey_opened:            { label: 'Survey',      cat: 'survey'     },
  survey_closed:            { label: 'Survey',      cat: 'survey'     },
  survey_updated:           { label: 'Survey',      cat: 'survey'     },
  survey_compiled:          { label: 'Survey',      cat: 'survey'     },
  survey_deleted:           { label: 'Survey',      cat: 'survey'     },
  survey_activated:         { label: 'Survey',      cat: 'survey'     },
  survey_deactivated:       { label: 'Survey',      cat: 'survey'     },
  client_onboarded:         { label: 'Client',      cat: 'client'     },
};

const CATEGORY_BADGE = {
  engagement: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  document:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  survey:     'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  client:     'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

// ── Upcoming Tasks tab ────────────────────────────────────────────
function UpcomingTasksTab({ navigate }) {
  const { data: tasks = [], isLoading, isError } = useUpcomingTasks();
  const [clientFilter,  setClientFilter]  = useState('');
  const [typeFilter,    setTypeFilter]    = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortBy,        setSortBy]        = useState('dueDate');

  const today = new Date().toISOString().slice(0, 10);
  const soonCutoff = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

  const clientOptions = useMemo(() => {
    const seen = {};
    tasks.forEach((t) => { seen[t.companyId] = t.companyName; });
    return Object.entries(seen)
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks]);

  const filtered = useMemo(() => {
    return tasks
      .filter((t) => {
        if (!showCompleted && t.completed) return false;
        if (clientFilter && t.companyId !== clientFilter) return false;
        if (typeFilter   && t.type !== typeFilter) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'client') return a.companyName.localeCompare(b.companyName) || a.dueDate.localeCompare(b.dueDate);
        return a.dueDate.localeCompare(b.dueDate);
      });
  }, [tasks, showCompleted, clientFilter, typeFilter, sortBy]);

  const isOverdue  = (t) => !t.completed && t.dueDate < today;
  const isDueToday = (t) => t.dueDate === today;
  const isDueSoon  = (t) => !t.completed && t.dueDate > today && t.dueDate <= soonCutoff;

  if (isLoading) return <p className="text-sm text-text-muted-light dark:text-text-muted py-6">Loading tasks…</p>;
  if (isError)   return <p className="text-sm text-red-500 py-6">Failed to load upcoming tasks.</p>;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
          <div className="sm:w-48">
            <Select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
              <option value="">All clients</option>
              {clientOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </div>
          <div className="sm:w-40">
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              <option value="phase_end">Phase ends</option>
            </Select>
          </div>
          <div className="sm:w-40">
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="dueDate">Sort: Due date</option>
              <option value="client">Sort: Client</option>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none ml-auto">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="rounded"
            />
            <span className="text-text-secondary-light dark:text-text-secondary">Show completed</span>
          </label>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-8">
            <CalendarDaysIcon className="w-8 h-8 mx-auto mb-2 text-text-muted-light dark:text-text-muted opacity-40" />
            <p className="text-sm text-text-secondary-light dark:text-text-secondary">
              {tasks.length === 0 ? 'No upcoming tasks — set phase end dates to see them here.' : 'No tasks match the current filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {filtered.map((task) => {
              const overdue  = isOverdue(task);
              const dueToday = isDueToday(task);
              const dueSoon  = isDueSoon(task);
              const dest = `/clients/${task.companyId}/engagement`;

              return (
                <button
                  key={task.id}
                  onClick={() => navigate(dest)}
                  className="w-full border border-border-light dark:border-border rounded-lg px-3 py-2.5 flex items-center gap-3 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    task.completed ? 'bg-green-400'
                    : overdue      ? 'bg-red-500'
                    : dueToday     ? 'bg-amber-500'
                    : dueSoon      ? 'bg-amber-300'
                    : 'bg-neutral-300 dark:bg-neutral-600'
                  }`} />

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${task.completed ? 'line-through text-text-muted-light dark:text-text-muted' : 'text-text-primary-light dark:text-text-primary'}`}>
                      {task.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-text-muted-light dark:text-text-muted">{task.companyName}</span>
                      <span className="text-xs text-text-muted-light dark:text-text-muted">·</span>
                      <span className="text-xs text-text-muted-light dark:text-text-muted">{task.phaseName}</span>
                    </div>
                  </div>

                  <span className="text-[11px] px-1.5 py-0 rounded font-medium flex-shrink-0 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    Phase end
                  </span>

                  <span className={`text-xs flex-shrink-0 font-medium tabular-nums ${
                    overdue  ? 'text-red-600 dark:text-red-400'
                    : dueToday ? 'text-amber-600 dark:text-amber-400'
                    : dueSoon  ? 'text-amber-500 dark:text-amber-300'
                    : 'text-text-muted-light dark:text-text-muted'
                  }`}>
                    {overdue ? 'Overdue · ' : ''}{formatDate(task.dueDate)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Audit Trail tab ───────────────────────────────────────────────
function AuditTrailTab({ clients, navigate }) {
  const [companyId,     setCompanyId]     = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const clientOptions = useMemo(() =>
    [...clients].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name })),
    [clients],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useActivitiesPaginated({ companyId: companyId || undefined });

  const activities = useMemo(() => {
    const all = data?.pages.flatMap((p) => p.activities) ?? [];
    if (!categoryFilter) return all;
    return all.filter((a) => (ACTIVITY_CATEGORY[a.type]?.cat ?? 'client') === categoryFilter);
  }, [data, categoryFilter]);

  if (isError) return <p className="text-sm text-red-500 py-6">Failed to load audit trail.</p>;

  return (
    <Card>
      <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
        <div className="sm:w-48">
          <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">All clients</option>
            {clientOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="sm:w-44">
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">All categories</option>
            <option value="engagement">Engagement</option>
            <option value="document">Documents</option>
            <option value="survey">Surveys</option>
            <option value="client">Client</option>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-muted-light dark:text-text-muted py-6">Loading audit trail…</p>
      ) : activities.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-text-secondary-light dark:text-text-secondary">No activity recorded yet.</p>
        </div>
      ) : (
        <div className="divide-y divide-border-light dark:divide-border">
          {activities.map((a) => {
            const meta = ACTIVITY_CATEGORY[a.type] || { label: a.type, cat: 'client' };
            const badgeCls = CATEGORY_BADGE[meta.cat] || CATEGORY_BADGE.client;
            return (
              <div key={a.id} className="py-2.5 flex items-start gap-3">
                <span className={`text-[11px] px-1.5 py-0 rounded font-medium flex-shrink-0 mt-0.5 ${badgeCls}`}>
                  {meta.label}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary-light dark:text-text-primary">{a.description}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {a.companyName && (
                      <button
                        onClick={() => navigate(`/clients/${a.companyId}`)}
                        className="text-xs text-text-muted-light dark:text-text-muted hover:opacity-70 transition cursor-pointer"
                      >
                        {a.companyName}
                      </button>
                    )}
                    {a.userId && (
                      <>
                        <span className="text-xs text-text-muted-light dark:text-text-muted">·</span>
                        <span className="text-xs text-text-muted-light dark:text-text-muted">{a.userId}</span>
                      </>
                    )}
                  </div>
                </div>
                <span className="text-xs text-text-muted-light dark:text-text-muted flex-shrink-0 tabular-nums">
                  {formatDate(a.timestamp)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {hasNextPage && (
        <div className="pt-3 text-center">
          <Button variant="secondary" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </Card>
  );
}

// ── Workshops tab ─────────────────────────────────────────────────
function PendingCard({ booking, navigate }) {
  const approve = useApproveWorkshop();
  const deny    = useDenyWorkshop();
  const [denyMode,   setDenyMode]   = useState(false);
  const [reason,     setReason]     = useState('');
  const [confirming, setConfirming] = useState(false);

  const handleApprove = async () => {
    if (!confirming) { setConfirming(true); return; }
    await approve.mutateAsync(booking.id);
    setConfirming(false);
  };

  const handleDeny = async () => {
    await deny.mutateAsync({ id: booking.id, reason });
    setDenyMode(false);
    setReason('');
  };

  return (
    <Card padding="md" className="border border-border">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium">{booking.organisationName}</p>
          <p className="text-xs text-text-muted mt-0.5">
            {booking.contactName && <span>{booking.contactName} · </span>}
            {booking.contactEmail}
          </p>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-text-muted">
            <span><span className="font-medium text-text-primary dark:text-text-primary">Date:</span> {booking.preferredDate} at {booking.preferredTime}</span>
            <span><span className="font-medium text-text-primary dark:text-text-primary">Location:</span> {booking.location}</span>
            <span>Submitted {formatDate(booking.submittedAt?.toDate ? booking.submittedAt.toDate() : booking.submittedAt)}</span>
          </div>
          {booking.notes && <p className="text-xs text-text-muted mt-1 italic">"{booking.notes}"</p>}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {!denyMode && !confirming && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setDenyMode(true)}>Deny</Button>
              <Button variant="primary" size="sm" onClick={handleApprove} disabled={approve.isPending}>
                Approve
              </Button>
            </>
          )}
          {confirming && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-muted">This will create a CMS client, portal user, and survey. Confirm?</span>
              <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleApprove} disabled={approve.isPending}>
                {approve.isPending ? 'Approving…' : 'Confirm'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {denyMode && (
        <div className="mt-3 pt-3 border-t border-border">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason for denial (internal note)..."
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-primary"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="secondary" size="sm" onClick={() => { setDenyMode(false); setReason(''); }}>Cancel</Button>
            <Button variant="secondary" size="sm" onClick={handleDeny} disabled={deny.isPending}>
              {deny.isPending ? 'Denying…' : 'Confirm Denial'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function WorkshopsTab({ navigate }) {
  const { data: bookings = [], isLoading } = useWorkshops();

  const pending  = useMemo(() => bookings.filter((b) => b.status === 'pending').sort((a, b) => {
    const ta = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt);
    const tb = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt);
    return ta - tb; // oldest first
  }), [bookings]);
  const approved = useMemo(() => bookings.filter((b) => b.status === 'approved'), [bookings]);
  const denied   = useMemo(() => bookings.filter((b) => b.status === 'denied'),   [bookings]);

  const thisMonth = useMemo(() => {
    const now = new Date();
    return approved.filter((b) => {
      const d = new Date(b.preferredDate);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
  }, [approved]);

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-text-muted">Loading workshops…</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Bookings', value: bookings.length },
          { label: 'Pending',        value: pending.length,  cls: pending.length > 0 ? 'text-amber-500' : '' },
          { label: 'Approved',       value: approved.length, cls: 'text-success' },
          { label: 'This Month',     value: thisMonth },
        ].map(({ label, value, cls }) => (
          <Card key={label} padding="sm">
            <p className="text-xs text-text-muted mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${cls || ''}`}>{value}</p>
          </Card>
        ))}
      </div>

      {/* Pending Requests */}
      <div>
        <h2 className="text-xs font-medium uppercase tracking-wider text-text-muted mb-3">
          Pending Requests {pending.length > 0 && <span className="ml-1 text-amber-500">({pending.length})</span>}
        </h2>
        {pending.length === 0 ? (
          <Card padding="md">
            <p className="text-sm text-text-muted text-center py-4">No pending booking requests.</p>
          </Card>
        ) : (
          <div className="flex flex-col gap-3">
            {pending.map((b) => <PendingCard key={b.id} booking={b} navigate={navigate} />)}
          </div>
        )}
      </div>

      {/* Approved Workshops */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-medium uppercase tracking-wider text-text-muted">Approved Workshops</h2>
        </div>
        {approved.length === 0 ? (
          <Card padding="md">
            <p className="text-sm text-text-muted text-center py-4">No approved workshops yet.</p>
          </Card>
        ) : (
          <Card padding="none">
            <Table
              cellPadding="compact"
              headers={[
                { label: 'Organisation' },
                { label: 'Date / Time' },
                { label: 'Location' },
                { label: 'Approved By' },
                { label: '' },
              ]}
              rows={approved.map((b) => ({
                cells: [
                  {
                    content: (
                      <div>
                        <p className="text-sm font-medium">{b.organisationName}</p>
                        <p className="text-xs text-text-muted">{b.contactEmail}</p>
                      </div>
                    ),
                  },
                  {
                    content: <span className="text-xs text-text-muted tabular-nums">{b.preferredDate} {b.preferredTime}</span>,
                  },
                  {
                    content: <span className="text-xs text-text-muted">{b.location}</span>,
                  },
                  {
                    content: <span className="text-xs text-text-muted">{b.approvedBy}</span>,
                  },
                  {
                    content: (
                      <div className="flex items-center gap-3 justify-end">
                        {b.calendarEventLink && (
                          // <a
                          //   href={b.calendarEventLink}
                          //   target="_blank"
                          //   rel="noopener noreferrer"
                          //   className="flex items-center gap-1 text-xs text-brand-primary hover:underline transition-colors flex-shrink-0"
                          // >
                          //   <CalendarDaysIcon className="w-3.5 h-3.5" />
                          //   Calendar
                          // </a>

                          <IconButton
                            icon={CalendarDaysIcon}
                            label="View calendar event"
                            variant="secondary"
                            size="sm"
                            display="icon"
                            onClick={() => window.open(b.calendarEventLink, '_blank')}
                            className="flex-shrink-0"
                          />
                        )}
                        {b.companyId && (
                          <IconButton
                            icon={UserCircleIcon}
                            label="View client"
                            variant="secondary"
                            size="sm"
                            display="icon"
                            onClick={() => navigate(`/clients/${b.companyId}`)}
                            className="flex-shrink-0"
                          />
                        )}
                      </div>
                    ),
                    className: 'text-right',
                  },
                ],
              }))}
            />
          </Card>
        )}
      </div>

      {/* Denied */}
      {denied.length > 0 && (
        <Accordion title={`Denied (${denied.length})`} defaultOpen={false}>
          <div className="divide-y divide-border">
            {denied.map((b) => (
              <div key={b.id} className="flex items-center gap-4 py-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{b.organisationName}</p>
                  <p className="text-xs text-text-muted">{b.contactEmail} · {b.preferredDate}</p>
                </div>
                {b.denialReason && (
                  <p className="text-xs text-text-muted italic">"{b.denialReason}"</p>
                )}
              </div>
            ))}
          </div>
        </Accordion>
      )}
    </div>
  );
}

// ── Templates Tab ───────────────────────────────────────────────
function TemplatesTab() {
  const { data: templates = [], isLoading } = useEngagementTemplates();
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formPhases, setFormPhases] = useState([{ name: '', activities: [''], deliverables: [''] }]);

  const resetForm = () => {
    setEditingId(null);
    setFormName('');
    setFormPhases([{ name: '', activities: [''], deliverables: [''] }]);
    setShowForm(false);
  };

  const startEditing = (t) => {
    setEditingId(t.id);
    setFormName(t.name || '');
    setFormPhases(
      (t.phases || []).map((p) => ({
        name: p.name || '',
        activities: p.activities?.length ? p.activities.map((a) => a.title || '') : [''],
        deliverables: p.deliverables?.length ? p.deliverables.map((d) => d.title || '') : [''],
      })),
    );
    setShowForm(true);
  };

  const addPhaseRow = () => setFormPhases(p => [...p, { name: '', activities: [''], deliverables: [''] }]);
  const removePhaseRow = (idx) => setFormPhases(p => p.filter((_, i) => i !== idx));
  const updatePhaseField = (idx, field, val) => setFormPhases(p => p.map((ph, i) => i === idx ? { ...ph, [field]: val } : ph));
  const addListItem = (phaseIdx, field) => setFormPhases(p => p.map((ph, i) => i === phaseIdx ? { ...ph, [field]: [...ph[field], ''] } : ph));
  const updateListItem = (phaseIdx, field, itemIdx, val) => setFormPhases(p => p.map((ph, i) =>
    i === phaseIdx ? { ...ph, [field]: ph[field].map((v, j) => j === itemIdx ? val : v) } : ph,
  ));
  const removeListItem = (phaseIdx, field, itemIdx) => setFormPhases(p => p.map((ph, i) =>
    i === phaseIdx ? { ...ph, [field]: ph[field].filter((_, j) => j !== itemIdx) } : ph,
  ));

  const handleSubmit = async () => {
    const name = formName.trim();
    if (!name) return;
    const phases = formPhases
      .filter(p => p.name.trim())
      .map(p => ({
        name: p.name.trim(),
        activities: p.activities.filter(a => a.trim()).map(a => ({ title: a.trim() })),
        deliverables: p.deliverables.filter(d => d.trim()).map(d => ({ title: d.trim() })),
      }));
    if (!phases.length) return;
    if (editingId) {
      await updateTemplate.mutateAsync({ id: editingId, name, phases });
    } else {
      await createTemplate.mutateAsync({ name, phases });
    }
    resetForm();
  };

  if (isLoading) return <div className="text-center py-12 text-sm text-text-muted-light dark:text-text-muted">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
          {templates.length} template{templates.length !== 1 ? 's' : ''}
        </p>
        <Button variant="primary" size="sm" onClick={() => showForm ? resetForm() : setShowForm(true)}>
          <PlusIcon className="w-4 h-4 -ml-0.5 mr-1 inline" />
          New Template
        </Button>
      </div>

      {showForm && (
        <Card className="p-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-text-muted-light dark:text-text-muted mb-1 block">Template Name</label>
            <input
              autoFocus
              value={formName}
              onChange={e => setFormName(e.target.value)}
              placeholder="e.g. AI Readiness Programme"
              className="w-full text-sm border border-border-light dark:border-border rounded-lg px-3 py-2 bg-transparent text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>

          {formPhases.map((phase, phaseIdx) => (
            <div key={phaseIdx} className="p-3 rounded-lg border border-border-light dark:border-border space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider">Phase {phaseIdx + 1}</span>
                {formPhases.length > 1 && (
                  <button onClick={() => removePhaseRow(phaseIdx)} className="ml-auto text-text-muted-light dark:text-text-muted hover:text-red-500 transition-colors">
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <input
                value={phase.name}
                onChange={e => updatePhaseField(phaseIdx, 'name', e.target.value)}
                placeholder="Phase name"
                className="w-full text-sm border border-border-light dark:border-border rounded-lg px-3 py-2 bg-transparent text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />

              <div>
                <span className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider">Activities</span>
                {phase.activities.map((a, i) => (
                  <div key={i} className="flex items-center gap-1 mt-1">
                    <input
                      value={a}
                      onChange={e => updateListItem(phaseIdx, 'activities', i, e.target.value)}
                      placeholder="Activity title"
                      className="flex-1 text-xs border border-border-light dark:border-border rounded px-2 py-1.5 bg-transparent text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    />
                    {phase.activities.length > 1 && (
                      <button onClick={() => removeListItem(phaseIdx, 'activities', i)} className="text-text-muted-light dark:text-text-muted hover:text-red-500 transition-colors">
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                <button onClick={() => addListItem(phaseIdx, 'activities')} className="text-xs text-brand-primary hover:underline mt-1">+ Add activity</button>
              </div>

              <div>
                <span className="text-[10px] font-bold text-text-muted-light dark:text-text-muted uppercase tracking-wider">Deliverables</span>
                {phase.deliverables.map((d, i) => (
                  <div key={i} className="flex items-center gap-1 mt-1">
                    <input
                      value={d}
                      onChange={e => updateListItem(phaseIdx, 'deliverables', i, e.target.value)}
                      placeholder="Deliverable title"
                      className="flex-1 text-xs border border-border-light dark:border-border rounded px-2 py-1.5 bg-transparent text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    />
                    {phase.deliverables.length > 1 && (
                      <button onClick={() => removeListItem(phaseIdx, 'deliverables', i)} className="text-text-muted-light dark:text-text-muted hover:text-red-500 transition-colors">
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                <button onClick={() => addListItem(phaseIdx, 'deliverables')} className="text-xs text-brand-primary hover:underline mt-1">+ Add deliverable</button>
              </div>
            </div>
          ))}

          <button onClick={addPhaseRow} className="text-xs text-brand-primary hover:underline">+ Add another phase</button>

          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={handleSubmit} disabled={createTemplate.isPending || updateTemplate.isPending}>
              {editingId
                ? (updateTemplate.isPending ? 'Saving…' : 'Save Changes')
                : (createTemplate.isPending ? 'Creating…' : 'Create Template')}
            </Button>
            <Button variant="secondary" size="sm" onClick={resetForm}>Cancel</Button>
          </div>
        </Card>
      )}

      {templates.length === 0 && !showForm ? (
        <div className="text-center py-16">
          <p className="text-text-muted-light dark:text-text-muted">No engagement templates yet.</p>
          <p className="text-xs text-text-muted-light dark:text-text-muted mt-1">Create a template to quickly set up engagement phases for new clients.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <Card key={t.id} padding="none">
              <div className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-text-primary-light dark:text-text-primary">{t.name}</p>
                  <p className="text-xs text-text-muted-light dark:text-text-muted">
                    {t.phases?.length || 0} phase{(t.phases?.length || 0) !== 1 ? 's' : ''}
                    {' · '}
                    {t.phases?.reduce((sum, p) => sum + (p.activities?.length || 0), 0)} activities
                    {' · '}
                    {t.phases?.reduce((sum, p) => sum + (p.deliverables?.length || 0), 0)} deliverables
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <IconButton
                    icon={PencilIcon}
                    label="Edit template"
                    variant="ghost"
                    size="sm"
                    onClick={() => startEditing(t)}
                    className="hover:text-brand-primary"
                  />
                  <IconButton
                    icon={TrashIcon}
                    label="Delete template"
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteTemplate.mutate(t.id)}
                    className="hover:text-red-500 dark:hover:text-red-400"
                  />
                </div>
              </div>
              <div className="px-4 pb-3">
                <div className="flex flex-wrap gap-1.5">
                  {t.phases?.map((p, i) => (
                    <span key={i} className="text-[11px] px-1.5 py-0 rounded font-medium bg-border-light text-text-muted-light dark:bg-surface-dark dark:text-text-muted">
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────
export default function Clients() {
  const navigate = useNavigate();
  const { data: clients = [], isLoading } = useClients();
  const [activeTab, setActiveTab] = useState('clients');

  return (
    <div className="container mx-auto px-4 py-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary">Clients</h1>
        {activeTab === 'clients' && (
          <IconButton
            icon={PlusIcon}
            label="Onboard New Client"
            variant="primary"
            size="md"
            display="icon"
            onClick={() => navigate('/cms/companies/new')}
          />
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border-light dark:border-border mb-6">
        <nav className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-text-secondary-light dark:text-text-secondary hover:opacity-75'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'clients'   && <ClientsTab clients={clients} isLoading={isLoading} navigate={navigate} />}
      {activeTab === 'surveys'   && <SurveysTab navigate={navigate} />}
      {activeTab === 'upcoming'  && <UpcomingTasksTab navigate={navigate} />}
      {activeTab === 'audit'     && <AuditTrailTab clients={clients} navigate={navigate} />}
      {activeTab === 'workshops' && <WorkshopsTab navigate={navigate} />}
      {activeTab === 'templates' && <TemplatesTab />}
    </div>
  );
}
